import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  ActionDependant,
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
} from '@ea-lab/reactive-json';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExt from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Link as LinkIcon, Link2Off, Loader2, Send, MessageSquarePlus, X,
} from 'lucide-react';
import { useMentionExtension } from '../../shared/hooks/useMentionExtension';
import { saveDocComment, fetchCollection } from '../../../services/api';

function processRawComments(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item?.data)
    .map((item) => {
      const d = item.data;
      return {
        id: d.id,
        body: d.body || '',
        author: d.author || '',
        pageId: d.pageId ? parseInt(d.pageId, 10) : null,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
      };
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

function ToolbarBtn({ active, onClick, children, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`p-1.5 rounded transition-colors ${active ? 'bg-stone-200 text-stone-800' : 'text-stone-500 hover:bg-stone-100 hover:text-stone-700'}`}
    >
      {children}
    </button>
  );
}

function MiniToolbar({ editor }) {
  if (!editor) return null;

  const setLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('URL du lien :');
    if (!url) return;
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        editor.chain().focus().setLink({ href: parsed.href }).run();
      }
    } catch { /* invalid URL — ignore */ }
  };

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-stone-200 bg-stone-50/80">
      <ToolbarBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} label="Gras">
        <Bold size={14} />
      </ToolbarBtn>
      <ToolbarBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italique">
        <Italic size={14} />
      </ToolbarBtn>
      <ToolbarBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} label="Souligné">
        <UnderlineIcon size={14} />
      </ToolbarBtn>
      <div className="w-px h-4 bg-stone-200 mx-1" />
      <ToolbarBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Liste à puces">
        <List size={14} />
      </ToolbarBtn>
      <ToolbarBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Liste numérotée">
        <ListOrdered size={14} />
      </ToolbarBtn>
      <div className="w-px h-4 bg-stone-200 mx-1" />
      <ToolbarBtn active={editor.isActive('link')} onClick={setLink} label="Lien">
        {editor.isActive('link') ? <Link2Off size={14} /> : <LinkIcon size={14} />}
      </ToolbarBtn>
    </div>
  );
}

/**
 * Self-contained comment form: toggle button, inline TipTap editor, submit.
 *
 * YAML usage:
 *   - type: DocCommentForm
 *     pageId: ~~.page.data.id
 *     commentsPath: "comments"
 */
export const DocCommentForm = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);

  const pageId = evaluateTemplateValue({
    valueToEvaluate: props.pageId,
    globalDataContext,
    templateContext,
  });

  const commentsPath = props.commentsPath || 'comments';

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const timerRef = useRef(null);

  const mentionExt = useMentionExtension();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false, underline: false }),
      LinkExt.configure({ openOnClick: false }),
      Underline,
      Placeholder.configure({ placeholder: 'Écrivez votre commentaire… (@ pour mentionner)' }),
      mentionExt,
    ],
    content: '',
  });

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      if (editor) editor.destroy();
    };
  }, [editor]);

  const handleSubmit = useCallback(async () => {
    if (status === 'sending' || !editor) return;

    const body = editor.getHTML();
    if (!body || body === '<p></p>') return;

    setStatus('sending');
    setErrorMsg('');
    clearTimeout(timerRef.current);

    try {
      await saveDocComment({ body, pageId: String(pageId) });
      editor.commands.clearContent();

      const params = new URLSearchParams({ display: 'decoupled', 'filter[0][f]': 'entity_id', 'filter[0][v]': String(pageId) });
      const raw = await fetchCollection(`/decoupled-api/comment/doc_comment/collection?${params}`, true);
      globalDataContext.updateData(processRawComments(raw), commentsPath);
      window.dispatchEvent(new CustomEvent('doc:comments-updated'));

      setStatus('success');
      timerRef.current = setTimeout(() => { setStatus(null); setOpen(false); }, 1200);
    } catch (e) {
      setErrorMsg(e.message || 'Erreur inconnue');
      setStatus('error');
      timerRef.current = setTimeout(() => setStatus(null), 6000);
    }
  }, [status, editor, pageId, commentsPath, globalDataContext]);

  const handleCancel = useCallback(() => {
    clearTimeout(timerRef.current);
    if (editor) editor.commands.clearContent();
    setOpen(false);
    setErrorMsg('');
    setStatus(null);
  }, [editor]);

  const isSending = status === 'sending';
  const isSuccess = status === 'success';
  const isError = status === 'error';

  if (!open) {
    return (
      <ActionDependant {...props}>
        <button
          type="button"
          data-doc-comment-form
          onClick={() => setOpen(true)}
          className="btn btn-secondary"
        >
          <MessageSquarePlus size={16} />
          Ajouter un commentaire
        </button>
      </ActionDependant>
    );
  }

  return (
    <ActionDependant {...props}>
      <div data-doc-comment-form-open className="border border-stone-200 rounded-xl overflow-hidden shadow-sm">
        <MiniToolbar editor={editor} />
        <EditorContent
          editor={editor}
          className="prose prose-sm prose-stone max-w-none p-3 min-h-24 focus-within:outline-none [&_.tiptap]:outline-none [&_.tiptap]:min-h-20"
        />
        <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-stone-50 border-t border-stone-200">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSending}
              className={`btn ${isSuccess ? 'btn-success' : 'btn-primary'}`}
            >
              {isSending ? (
                <><Loader2 size={14} className="animate-spin" /> Envoi…</>
              ) : isSuccess ? (
                <>Commentaire ajouté !</>
              ) : (
                <><Send size={14} /> Commenter</>
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-ghost"
            >
              <X size={14} /> Annuler
            </button>
          </div>
          {isError && (
            <span className="text-xs text-red-600 text-right">{errorMsg}</span>
          )}
        </div>
      </div>
    </ActionDependant>
  );
};
